import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { planServices } from "../services/PlanServices";
import { walletServices } from "../services/WalletServices";
import "bootstrap/dist/css/bootstrap.min.css";
import { Modal, Button } from "react-bootstrap";
import styled from "styled-components";

const Plans = () => {
  const [investmentAmount, setInvestmentAmount] = useState(5000);
  const [inputAmount, setInputAmount] = useState("5000");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const userId = localStorage.getItem('id');
  const [investmentPlans, setInvestmentPlans] = useState([]);
  const [walletData, setWalletData] = useState([]);

  // Define slider limits
  const SLIDER_MIN = 5000;
  const SLIDER_MAX = 200000;

  useEffect(() => {
      getCategoryList();
      getWalletData(userId);
  }, [userId]);

  const getWalletData = async (userId) => {
    try {
      const getWalletResponse =
        await walletServices.getWalletDetails(userId);
      if (getWalletResponse.status === 200) {
        const   walletDetails = getWalletResponse?.data;
        setWalletData(walletDetails);
      } else {
        toast.error(getWalletResponse.data.message, "error");
      }
    } catch (error) {
      toast.error(error.response.data.message, "error");
    }
  };

  const getCategoryList = async () => {
    try {
      const getPlansResponse =
        await planServices.getAllPlans();
      if (getPlansResponse.status === 200) {
        const plansList = getPlansResponse?.data?.data;
        setInvestmentPlans(plansList);
      } else {
        toast.error(getPlansResponse.data.message, "error");
      }
    } catch (error) {
      toast.error(error.response.data.message, "error");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getEligiblePlans = (amount) => {
    const eligiblePlans = investmentPlans.filter(
      (plan) =>
        amount >= plan.investmentAmount.min &&
        (plan.investmentAmount.max === null ||
          amount <= plan.investmentAmount.max)
    );

    // If no plans are eligible and amount is high, find the highest tier plan (usually VIP)
    if (eligiblePlans.length === 0 && amount > 0) {
      const sortedPlans = [...investmentPlans].sort((a, b) => {
        // Sort by minimum investment amount in descending order
        return b.investmentAmount.min - a.investmentAmount.min;
      });
      
      // Return the highest tier plan if the amount exceeds all maximum limits
      if (sortedPlans.length > 0) {
        const highestPlan = sortedPlans[0];
        // Check if the amount is at least the minimum for the highest plan
        if (amount >= highestPlan.investmentAmount.min) {
          return [highestPlan];
        }
      }
    }

    return eligiblePlans;
  };

  const eligiblePlans = getEligiblePlans(investmentAmount);
  const currentPlan = eligiblePlans.length > 0 ? eligiblePlans[0] : null;

  const handleSliderChange = (e) => {
    const value = parseInt(e.target.value);
    setInvestmentAmount(value);
    setInputAmount(value.toString());
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    
    // Allow empty input while typing
    if (value === '') {
      setInputAmount('');
      return;
    }

    // Only allow numeric input
    if (!/^\d+$/.test(value)) {
      return;
    }

    const numericValue = parseInt(value);
    
    // Set minimum limit
    if (numericValue < SLIDER_MIN) {
      setInputAmount(value);
      return;
    }

    setInputAmount(value);
    setInvestmentAmount(numericValue);
  };

  const handleInputBlur = () => {
    // Handle empty input on blur
    if (inputAmount === '' || parseInt(inputAmount) < SLIDER_MIN) {
      setInputAmount(SLIDER_MIN.toString());
      setInvestmentAmount(SLIDER_MIN);
      return;
    }

    const numericValue = parseInt(inputAmount);
    setInvestmentAmount(numericValue);
  };

  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  // Check wallet balance before allowing plan selection
  const checkWalletBalance = (requiredAmount) => {
    const currentBalance = walletData?.balance || 0;
    return currentBalance >= requiredAmount;
  };

  const handleSelectPlan = (plan) => {
    // Check wallet balance first
    if (!checkWalletBalance(investmentAmount)) {
      toast.error(
        `Insufficient wallet balance. Your current balance is ${formatCurrency(walletData?.balance || 0)} but you need ${formatCurrency(investmentAmount)} for this investment.`,
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        }
      );
      return;
    }

    // If balance is sufficient, proceed with plan selection
    setSelectedPlan(plan);
    setShowModal(true);
  };

  const calculateMaturityDate = (plan) => {
    if (!plan) return new Date().toLocaleDateString();

    const today = new Date();
    let maturityDate = new Date(today);

    if (plan.lockInPeriodUnit === "days") {
      maturityDate.setDate(today.getDate() + plan.lockInPeriod);
    } else {
      maturityDate.setFullYear(today.getFullYear() + plan.lockInPeriod);
    }

    return maturityDate.toLocaleDateString();
  };

  // Generate a unique transaction ID
  const generateTransactionId = () => {
    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * 1000000);
    return `txn-${timestamp}-${randomNum}`;
  };

  // Calculate expected return
  const calculateExpectedReturn = (plan, amount) => {
    const avgProfitPercentage = (plan.profitPercentage.min + plan.profitPercentage.max) / 2;
    return Math.round(amount + (amount * avgProfitPercentage / 100));
  };

  // Get maturity date in ISO format
  const getMaturityDateISO = (plan) => {
    if (!plan) return new Date().toISOString();

    const today = new Date();
    let maturityDate = new Date(today);

    if (plan.lockInPeriodUnit === "days") {
      maturityDate.setDate(today.getDate() + plan.lockInPeriod);
    } else {
      maturityDate.setFullYear(today.getFullYear() + plan.lockInPeriod);
    }

    return maturityDate.toISOString();
  };

  const handleConfirmInvestment = async () => {
    // Double-check wallet balance before final confirmation
    if (!checkWalletBalance(investmentAmount)) {
      toast.error(
        "Insufficient wallet balance. Please check your wallet and try again.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
      setShowModal(false);
      return;
    }
    
    // Check if planId exists in different possible field names
    const planId = selectedPlan.id || selectedPlan._id || selectedPlan.planId;
    
    if (!planId) {
      console.error("Plan ID not found in selectedPlan object:", selectedPlan);
      toast.error("Plan ID is missing. Please try selecting the plan again.", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }

    // Prepare the payload according to the specified structure
    const purchasePayload = {
      userId: userId,
      planId: planId,
      planCategory: selectedPlan.category,
      planType: selectedPlan.planType,
      investmentAmount: investmentAmount,
      profitPercentage: {
        min: selectedPlan.profitPercentage.min,
        max: selectedPlan.profitPercentage.max
      },
      lockInPeriod: selectedPlan.lockInPeriod,
      lockInPeriodUnit: selectedPlan.lockInPeriodUnit,
      maturityDate: getMaturityDateISO(selectedPlan),
      expectedReturn: calculateExpectedReturn(selectedPlan, investmentAmount),
      transactionId: generateTransactionId()
    };

    try {
      // Submit the plan purchase with the complete payload
      const response = await planServices.purchasePlan(purchasePayload);
      
      if (response.status === 200 || response.status === 201) {
        setShowModal(false);
        toast.success(
          `Investment of ${formatCurrency(investmentAmount)} confirmed successfully!`,
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
        
        // Refresh wallet data after successful purchase
        getWalletData(userId);
      } else {
        toast.error(
          response.data?.message || "Failed to process investment. Please try again.",
          {
            position: "top-right",
            autoClose: 5000,
          }
        );
      }
    } catch (error) {
      console.error("Investment error:", error);
      toast.error(
        error.response?.data?.message || "An error occurred while processing your investment.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    }
  };

  return (
    <>
      <div className="body d-flex py-3">
        <div className="container-xxl">
          <div className="row align-items-center">
            <div className="border-0 mb-4">
              <div className="card-header py-3 no-bg bg-transparent d-flex align-items-center px-0 justify-content-between border-bottom flex-wrap">
                <h3 className="fw-bold mb-0">Plans</h3>
                {/* Display wallet balance */}
                <div className="wallet-balance">
                  <span className="badge bg-success fs-6">
                    Wallet Balance: {formatCurrency(walletData?.balance || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="row align-item-center">
            <div className="col-md-12">
              <div className="card mb-3">
                <div className="card-body">
                  <InvestmentContainer>
                    <h2 className="text-center mb-4">
                      Investment Plan Selector
                    </h2>
                    <p className="text-center text-muted mb-5">
                      Use the slider or input field to select your investment amount and view available plans
                    </p>

                    <InvestmentStats>
                      <StatItem>
                        <StatValue id="selected-amount" className="text-warning">
                          {formatCurrency(investmentAmount)}
                        </StatValue>
                        <StatLabel>INVESTMENT AMOUNT</StatLabel>
                      </StatItem>
                      <StatItem>
                        <StatValue id="selected-profit" className="text-warning">
                          {currentPlan
                            ? `${currentPlan.profitPercentage.min}-${currentPlan.profitPercentage.max}%`
                            : "N/A"}
                        </StatValue>
                        <StatLabel>PROFIT RANGE</StatLabel>
                      </StatItem>
                      <StatItem>
                        <StatValue id="selected-period" className="text-warning">
                          {currentPlan
                            ? `${currentPlan.lockInPeriod} ${currentPlan.lockInPeriodUnit}`
                            : "N/A"}
                        </StatValue>
                        <StatLabel>LOCK-IN PERIOD</StatLabel>
                      </StatItem>
                    </InvestmentStats>

                    {/* Manual Input Section */}
                    <InputSection>
                      <InputLabel>Enter Investment Amount</InputLabel>
                      <InputWrapper>
                        <CurrencySymbol>$</CurrencySymbol>
                        <AmountInput
                          type="text"
                          value={inputAmount}
                          onChange={handleInputChange}
                          onBlur={handleInputBlur}
                          onKeyPress={handleInputKeyPress}
                          placeholder="Enter amount"
                        />
                      </InputWrapper>
                      <InputHint>
                        Minimum: {formatCurrency(SLIDER_MIN)} | For amounts above {formatCurrency(SLIDER_MAX)}, VIP plans may be available
                      </InputHint>
                    </InputSection>

                    <SliderContainer>
                      <SliderLabel>Or use the slider (${SLIDER_MIN.toLocaleString()} - ${SLIDER_MAX.toLocaleString()})</SliderLabel>
                      <input
                        type="range"
                        className="form-range"
                        id="investmentSlider"
                        min={SLIDER_MIN}
                        max={SLIDER_MAX}
                        step="1000"
                        value={Math.min(investmentAmount, SLIDER_MAX)}
                        onChange={handleSliderChange}
                        disabled={investmentAmount > SLIDER_MAX}
                      />
                      <div className="d-flex justify-content-between mt-2">
                        <span>${SLIDER_MIN.toLocaleString()}</span>
                        <span>${SLIDER_MAX.toLocaleString()}+</span>
                      </div>
                      {investmentAmount > SLIDER_MAX && (
                        <SliderDisabledNote>
                          Slider disabled for amounts above ${SLIDER_MAX.toLocaleString()}. Use the input field above.
                        </SliderDisabledNote>
                      )}
                    </SliderContainer>

                    <div id="planDetails" className="mt-4">
                      {eligiblePlans.length > 0 ? (
                        eligiblePlans.map((plan, index) => {
                          const potentialReturn = (
                            (investmentAmount *
                              (plan.profitPercentage.min + plan.profitPercentage.max)) /
                            2 /
                            100
                          ).toFixed(2);
                          const isVip = plan.planType === "vip";
                          const hasInsufficientBalance = !checkWalletBalance(investmentAmount);
                          const isHighAmountPlan = investmentAmount > (plan.investmentAmount.max || 0) && plan.investmentAmount.max !== null;

                          return (
                            <PlanCard
                              key={index}
                              isVip={isVip}
                              className={`plan-card ${isVip ? "vip" : ""} ${hasInsufficientBalance ? "insufficient-balance" : ""}`}
                            >
                              <PlanTitle className="plan-title">
                                <div>
                                  <h4>
                                    {plan.category}
                                    {isVip && (
                                      <VipBadge className="vip-badge">
                                        VIP
                                      </VipBadge>
                                    )}
                                    {isHighAmountPlan && (
                                      <HighAmountBadge className="high-amount-badge">
                                        HIGH AMOUNT
                                      </HighAmountBadge>
                                    )}
                                  </h4>
                                  <div>
                                    <ProfitBadge className="profit-badge">
                                      {plan.profitPercentage.min}-{plan.profitPercentage.max}% profit
                                    </ProfitBadge>
                                    <TimeBadge className="time-badge ms-2">
                                      {plan.lockInPeriod}{" "}
                                      {plan.lockInPeriodUnit}
                                    </TimeBadge>
                                  </div>
                                </div>
                              </PlanTitle>

                              <PlanDetails className="plan-details">
                                <DetailItem className="detail-item">
                                  <DetailLabel className="detail-label">
                                    INVESTMENT RANGE
                                  </DetailLabel>
                                  <DetailValue className="detail-value">
                                    {formatCurrency(plan.investmentAmount.min)} -{" "}
                                      {plan.investmentAmount.max
                                        ? formatCurrency(plan.investmentAmount.max)
                                      : "No Limit"}
                                  </DetailValue>
                                </DetailItem>
                                <DetailItem className="detail-item">
                                  <DetailLabel className="detail-label">
                                    POTENTIAL RETURN
                                  </DetailLabel>
                                  <DetailValue className="detail-value">
                                    {formatCurrency(potentialReturn)}
                                  </DetailValue>
                                </DetailItem>
                                <DetailItem className="detail-item">
                                  <DetailLabel className="detail-label">
                                    MATURITY DATE
                                  </DetailLabel>
                                  <DetailValue className="detail-value">
                                    {calculateMaturityDate(plan)}
                                  </DetailValue>
                                </DetailItem>
                              </PlanDetails>

                              {isHighAmountPlan && (
                                <div className="alert alert-info mt-3 mb-3">
                                  <small>
                                    <strong>High Amount Investment:</strong> Your investment of {formatCurrency(investmentAmount)} exceeds the typical maximum for this plan, but it's still eligible for the same terms.
                                  </small>
                                </div>
                              )}

                              {hasInsufficientBalance && (
                                <div className="alert alert-danger mt-3 mb-3">
                                  <small>
                                    <strong>Insufficient Balance:</strong> You need {formatCurrency(investmentAmount)} but only have {formatCurrency(walletData?.balance || 0)} in your wallet.
                                  </small>
                                </div>
                              )}

                              <div className="mt-4">
                                <SelectButton
                                  className={`btn ${hasInsufficientBalance ? 'btn-secondary' : 'btn-primary'}`}
                                  onClick={() => handleSelectPlan(plan)}
                                  disabled={hasInsufficientBalance}
                                  style={{
                                    opacity: hasInsufficientBalance ? 0.6 : 1,
                                    cursor: hasInsufficientBalance ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  {hasInsufficientBalance ? 'Insufficient Balance' : 'Select This Plan'}
                                </SelectButton>
                              </div>
                            </PlanCard>
                          );
                        })
                      ) : (
                        <div className="alert alert-warning">
                          No investment plans available for the selected amount of {formatCurrency(investmentAmount)}.
                          Please adjust your investment amount to at least {formatCurrency(SLIDER_MIN)}.
                        </div>
                      )}
                    </div>
                  </InvestmentContainer>
                </div>

                {/* Custom Modal Component */}
                <Modal show={showModal}>
                  <ModalContent>
                    <ModalHeader>
                      <h4>Confirm Investment Plan</h4>
                      <CloseButton onClick={() => setShowModal(false)}>
                        &times;
                      </CloseButton>
                    </ModalHeader>
                    <ModalBody>
                      {selectedPlan && (
                        <>
                          <div className="mb-3">
                            <div className="alert alert-info">
                              <strong>Wallet Balance:</strong> {formatCurrency(walletData?.balance || 0)}
                            </div>
                          </div>
                          <p>
                            You are about to invest{" "}
                            {formatCurrency(investmentAmount)} in:
                          </p>
                          <h5>{selectedPlan.category}</h5>
                          <p>
                            <strong>Profit Range:</strong>{" "}
                            {selectedPlan.profitPercentage.min}-{selectedPlan.profitPercentage.max}%
                            <br />
                            <strong>Lock-in Period:</strong>{" "}
                            {selectedPlan.lockInPeriod}{" "}
                            {selectedPlan.lockInPeriodUnit}
                            <br />
                            <strong>Potential Return:</strong>{" "}
                            {formatCurrency(
                              (investmentAmount *
                                (selectedPlan.profitPercentage.min +
                                  selectedPlan.profitPercentage.max)) /
                                2 /
                                100
                            )}
                            <br />
                            <strong>Maturity Date:</strong>{" "}
                            {calculateMaturityDate(selectedPlan)}
                          </p>
                          <p>
                            Are you sure you want to proceed with this
                            investment?
                          </p>
                        </>
                      )}
                    </ModalBody>
                    <ModalFooter>
                      <SecondaryButton onClick={() => setShowModal(false)}>
                        Cancel
                      </SecondaryButton>
                      <PrimaryButton onClick={handleConfirmInvestment}>
                        Confirm Investment
                      </PrimaryButton>
                    </ModalFooter>
                  </ModalContent>
                </Modal>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Plans;

// Existing styled components...
const InvestmentContainer = styled.div`
  max-width: 800px;
  margin: auto;
  padding: 30px;
  background-color: #fff;
  border-radius: 10px;
`;

const SliderContainer = styled.div`
  margin: 20px 0;
`;

const SliderLabel = styled.div`
  font-size: 0.9rem;
  color: #6c757d;
  margin-bottom: 10px;
  text-align: center;
`;

const SliderDisabledNote = styled.div`
  font-size: 0.8rem;
  color: #dc3545;
  text-align: center;
  margin-top: 10px;
  font-style: italic;
`;

// New styled components for input section
const InputSection = styled.div`
  margin: 30px 0;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 2px solid #e9ecef;
`;

const InputLabel = styled.label`
  display: block;
  font-weight: 600;
  color: #495057;
  margin-bottom: 10px;
  font-size: 1.1rem;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  max-width: 300px;
  margin: 0 auto;
`;

const CurrencySymbol = styled.span`
  position: absolute;
  left: 15px;
  font-size: 1.2rem;
  font-weight: 600;
  color: #6c757d;
  z-index: 1;
`;

const AmountInput = styled.input`
  width: 100%;
  padding: 12px 15px 12px 35px;
  font-size: 1.2rem;
  font-weight: 600;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  text-align: center;
  background-color: #fff;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #feb019;
    box-shadow: 0 0 0 0.2rem rgba(254, 176, 25, 0.25);
  }

  &::placeholder {
    color: #adb5bd;
    font-weight: normal;
  }
`;

const InputHint = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
  text-align: center;
  margin-top: 8px;
  line-height: 1.4;
`;

const PlanCard = styled.div`
  background-color: #f8f9fa;
  border-left: 5px solid ${(props) => (props.isVip ? "#ffc107" : "#feb019")};
  padding: 20px;
  border-radius: 5px;
  margin-top: 30px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const PlanTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
`;

const PlanDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 20px;
`;

const DetailItem = styled.div`
  flex: 1 0 33%;
  margin-bottom: 15px;
`;

const DetailLabel = styled.div`
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 5px;
`;

const DetailValue = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
`;

const Badge = styled.span`
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.9rem;
`;

const ProfitBadge = styled(Badge)`
  background-color: #d1e7dd;
  color: #0f5132;
`;

const TimeBadge = styled(Badge)`
  background-color: #e2e3e5;
  color: #41464b;
`;

const VipBadge = styled(Badge)`
  background-color: #ffc107;
  color: #856404;
  margin-left: 10px;
`;

const HighAmountBadge = styled(Badge)`
  background-color: #6f42c1;
  color: #fff;
  margin-left: 10px;
`;

const InvestmentStats = styled.div`
  display: flex;
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const StatItem = styled.div`
  flex: 1;
  text-align: center;
  padding: 0 10px;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #feb019;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #6c757d;
`;

const SelectButton = styled.button`
  width: 100%;
  background-color: #feb019;
  border: none;
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: #feb019;
  }
`;

const ModalContent = styled.div`
  background-color: #fefefe;
  margin: 10% auto;
  padding: 20px;
  border: 1px solid #888;
  width: 80%;
  max-width: 500px;
  border-radius: 0.3rem;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 15px;
  border-bottom: 1px solid #dee2e6;

  h4 {
    margin: 0;
  }
`;

const CloseButton = styled.span`
  color: #aaa;
  font-size: 28px;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    color: black;
  }
`;

const ModalBody = styled.div`
  padding: 15px 0;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 15px;
  border-top: 1px solid #dee2e6;
  gap: 10px;
`;

const SecondaryButton = styled(Button)`
  background-color: #feb019;
  color: white;

  &:hover {
    background-color: #feb019;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: #feb019;
  color: white;

  &:hover {
    background-color: #feb019;
  }
`;